import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MdDescription, MdVerifiedUser, MdSecurity, MdTrendingUp, MdTrendingDown, MdInfo } from "react-icons/md";
import { FaInfoCircle } from "react-icons/fa";
import { FiActivity } from "react-icons/fi";
import { DashboardSummary } from "../../../utils/hooks/dashboard/useDashboardSummary";

interface ModernStatSectionProps {
  data: DashboardSummary;
  isSubscriptionOnly?: boolean;
  selectedYear?: number | null;
  subscriptionDates?: { startDate: string; endDate: string } | null;
}

const INFO_MAPPING: Record<string, string> = {
  "Total Reports Received": "Total number of security vulnerability reports submitted by researchers across all your programs (includes accepts risk/non-actionable reports).",
  "Reports Resolved": "Number of vulnerabilities that have been successfully fixed and verified.",
  "Active Testing Programs": "The number of active security testing programs currently running.",
  "Open Vulnerabilities": "Security issues that are currently being fixed or awaiting review by your team."
};

const ModernStatSection: React.FC<ModernStatSectionProps> = ({ 
  data, 
  isSubscriptionOnly = false, 
  selectedYear = null,
  subscriptionDates = null
}) => {
  // Sub-metric label changes based on subscription mode and age
  let last30Label = "Last 30 Days";
  const currentYear = new Date().getFullYear();
  
  if (selectedYear && selectedYear !== currentYear) {
    last30Label = `Added in ${selectedYear}`;
  } else if (isSubscriptionOnly && subscriptionDates) {
    const subStart = new Date(subscriptionDates.startDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - subStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If the subscription is less than 30 days old, call it "First Month"
    // Otherwise, call it "Last 30 Days" to show the current monthly trend
    if (diffDays <= 30) {
      last30Label = "First Month";
    } else {
      last30Label = "Last 30 Days";
    }
  } else if (isSubscriptionOnly) {
    // Fallback if dates are missing but toggle is on
    last30Label = "First Month";
  }

  const cards = React.useMemo(() => [
    {
      title: "Total Reports Received",
      value: data.totalReports.current,
      trend: null,
      trendLabel: last30Label,
      customTrendValue: (() => {
        const change = (data.totalReports as any).change;
        if (change === undefined || change === null) return "New";
        return change >= 0 ? `+${change}` : `${change}`;
      })(),
      icon: <img src="/icons/newb1.svg" className="h-6 w-6" alt="Reports" loading="eager" decoding="async" />,
      iconBg: "bg-[#EFF6FF]",
      isPositiveGood: true,
      useTrendIcon: false,
    },
    {
      title: "Reports Resolved",
      value: data.resolvedIssues.current,
      trend: null,
      trendLabel: "resolution rate",
      customTrendValue: data.totalReports.current > 0
        ? `${Math.round((data.resolvedIssues.current / data.totalReports.current) * 100)}%`
        : "0%",
      icon: <img src="/icons/newb2.svg" className="h-6 w-6" alt="Resolved" loading="eager" decoding="async" />,
      iconBg: "bg-[#F0FDF4]",
      isPositiveGood: true,
      useTrendIcon: false,
    },
    {
      title: "Active Testing Programs",
      value: data.totalPrograms.current,
      trend: null,
      customTrendValue: (() => {
        const change = (data.totalPrograms as any).change;
        if (change === undefined || change === null) return null;
        return change >= 0 ? `+${change}` : `${change}`;
      })(),
      trendLabel: last30Label,
      icon: <img src="/icons/newb3.svg" className="h-6 w-6" alt="Programs" loading="eager" decoding="async" />,
      iconBg: "bg-[#EFF6FF]",
      isPositiveGood: true,
      useTrendIcon: false,
    },
    {
      title: "Open Vulnerabilities",
      value: data.activeIssues?.current || 0,
      trend: null,
      customTrendValue: String(data.awaitingReview || 0),
      trendLabel: "awaiting review",
      useTrendIcon: false,
      icon: <img src="/icons/newb1.svg" className="h-6 w-6" alt="Active Reports" loading="eager" decoding="async" />,
      iconBg: "bg-[#EFF6FF]",
      isPositiveGood: false,
      info: "Security issues that are currently being fixed or awaiting review by your team."
    }
  ], [data, last30Label]);

  const [hoveredTooltip, setHoveredTooltip] = React.useState<string | null>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => {
        const trendValue = card.trend;
        const isPositive = (trendValue ?? 0) >= 0;
        const trendColor = card.isPositiveGood === isPositive ? "text-emerald-500" : "text-rose-500";

        return (
          <motion.div
            key={card.title}
            className="bg-white rounded-xl pt-4 pb-10 px-4 shadow-sm border border-slate-100 flex items-start gap-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, duration: 0.2 }}
          >
            {/* Icon Container */}
            <div className={`p-3 rounded-xl ${card.iconBg} shrink-0`}>
              {React.cloneElement(card.icon as React.ReactElement, { className: "h-6 w-6" })}
            </div>

            {/* Content Container */}
            <div className="flex-1 flex flex-col min-w-0 mt-0.5">
              <div className="flex items-center gap-1.5 relative group">
                <p className="text-[#6B7280] text-[13px] font-medium tracking-tight truncate">
                  {card.title}
                </p>
                <div className="relative">
                  <MdInfo
                    className="h-3.5 w-3.5 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
                    onMouseEnter={() => setHoveredTooltip(card.title)}
                    onMouseLeave={() => setHoveredTooltip(null)}
                  />
                  <AnimatePresence>
                    {hoveredTooltip === card.title && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className={`absolute bottom-full mb-2 z-50 w-48 bg-[#010D2C] text-white p-2.5 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none ${index === 3 ? "right-0" : "left-0"
                          }`}
                      >
                        {INFO_MAPPING[card.title]}
                        <div className={`absolute top-full border-8 border-transparent border-t-[#010D2C] ${index === 3 ? "right-2" : "left-2"
                          }`} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="flex items-baseline gap-3 mt-1">
                <h3 className="text-2xl font-black text-slate-800 leading-none">
                  {card.value.toLocaleString()}
                </h3>

                {/* Trend / Sub-metric Section */}
                {!(selectedYear && selectedYear !== currentYear) && (
                  <div className="flex flex-col leading-tight">
                    <div className={`flex items-center gap-0.5 font-black text-[14px] ${card.useTrendIcon !== false ? trendColor : "text-[#58CC02]"}`}>
                      {card.useTrendIcon !== false && (
                        isPositive ? <MdTrendingUp className="h-3 w-3" /> : <MdTrendingDown className="h-3 w-3" />
                      )}
                      <span>
                        {card.customTrendValue
                          ? card.customTrendValue
                          : trendValue === null || trendValue === undefined
                            ? "New"
                            : `${isPositive ? '+' : ''}${Math.round(trendValue)}%`}
                      </span>
                    </div>
                    <p className={`text-[11px] font-bold tracking-tighter ${card.useTrendIcon !== false ? trendColor : "text-[#58CC02]"} opacity-90 capitalize`}>
                      {card.trendLabel}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default React.memo(ModernStatSection);

